import { useEffect, useState } from "react";
import { BadgeCheck, PenLine } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, SectionHeading, Stars } from "@/components/common/ui";
import { ReviewForm } from "@/components/home/ReviewForm";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolve = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

export const Testimonials = ({ initial = 8 }) => {
  const [reviews, setReviews] = useState([]);
  const [visible, setVisible] = useState(initial);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const shown = reviews.slice(0, visible);

  return (
    <section id="reviews" className="bg-[#FFFCFA] py-24 md:py-32" data-testid="testimonials-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="flex flex-col items-center gap-3 text-center">
          <SectionHeading align="center" overline="Real Glow Stories" title="Glowing Reviews" />
          <div className="flex items-center gap-2">
            <Stars n={5} className="h-5 w-5" />
            <span className="text-sm font-medium text-[#2C2B2C]">4.9 · 50+ verified reviews</span>
          </div>
          <button onClick={() => setFormOpen(true)} data-testid="open-review-form"
            className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#2C2B2C] px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-[#2C2B2C] transition-all hover:bg-[#2C2B2C] hover:text-[#FFFCFA]">
            <PenLine className="h-4 w-4" /> Write a Review
          </button>
        </div>

        <div className="mt-14 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {shown.map((r, i) => (
            <Reveal key={r.id || i} delay={(i % 4) * 0.06}
              className="w-[82%] shrink-0 snap-start overflow-hidden rounded-2xl border border-[#F2E8E6] bg-white sm:w-[300px] lg:w-[320px]">
              {r.photo && (
                <img src={resolve(r.photo)} alt={`${r.name}'s result`} loading="lazy"
                  className="aspect-square w-full object-cover" data-testid={`review-photo-${i}`} />
              )}
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <Stars n={r.rating} className="h-3.5 w-3.5" />
                  {r.verified && (
                    <span className="inline-flex items-center gap-1 text-[0.6rem] font-semibold uppercase tracking-wider text-[#3B82F6]">
                      <BadgeCheck className="h-3.5 w-3.5" /> Verified Customer
                    </span>
                  )}
                </div>
                <p className="mt-3 font-display text-base text-[#2C2B2C]">"{r.title}"</p>
                <p className="mt-1.5 text-[0.8rem] font-light leading-relaxed text-[#8A7F86]">{r.text}</p>
                <div className="mt-4 border-t border-[#F2E8E6] pt-3">
                  <p className="text-xs font-semibold text-[#2C2B2C]">{r.name}</p>
                  <p className="text-[0.65rem] font-light text-[#8A7F86]">{[r.location, r.days].filter(Boolean).join(" · ")}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {visible < reviews.length && false && (
          <div className="mt-12 text-center" />
        )}
      </div>

      <ReviewForm open={formOpen} onClose={() => setFormOpen(false)}
        onSubmitted={(rev) => setReviews((prev) => [rev, ...prev])} />
    </section>
  );
};